import { useCallback } from "react";

import {
  useConfirmEdiUploadBatchMutation,
  useInitiateEdiUploadBatchMutation,
} from "../store/queries/ediConversion";
import { IEdiUploadMapping } from "../types/ediConversionFile";

// Must match the Content-Type the backend bakes into the pre-signed URL signature in
// initiateUploadBatch (edi_conversion.service.ts). If these drift apart S3 rejects the
// PUT with a SignatureDoesNotMatch error.
const S3_PUT_CONTENT_TYPE = "application/octet-stream";

export interface IDirectUploadProgress {
  // Aggregate bytes uploaded across the 835 (and optional 837) PUT phase.
  loaded: number;
  total: number;
  // Which file is currently in-flight, for UX strings ("Uploading 835…" vs "Uploading 837…").
  currentLabel: "835" | "837";
}

export interface IDirectUploadParams {
  file: File;
  file837?: File;
  displayName: string;
  description?: string;
  mappings: IEdiUploadMapping[];
  onProgress?: (progress: IDirectUploadProgress) => void;
}

export interface IDirectUploadResult {
  uploadBatchId: string;
  ediConversionFileIds: string[];
}

// Wraps XMLHttpRequest in a promise — fetch() does not expose upload progress in browsers,
// so XHR is the only way to drive a progress bar during a S3 PUT.
function putToS3WithProgress(
  url: string,
  file: File,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", S3_PUT_CONTENT_TYPE);
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event.loaded, event.total);
      }
    });
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        // S3 returns XML on error; slice it down so it doesn't blow up the toast.
        reject(
          new Error(
            `S3 PUT failed (${xhr.status}): ${xhr.responseText.slice(0, 240)}`
          )
        );
      }
    };
    xhr.onerror = () =>
      reject(
        new Error(
          "Network error during S3 upload. Check your connection and try again."
        )
      );
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.send(file);
  });
}

/**
 * Three-step direct-to-S3 EDI upload.
 *   1. POST /edi-conversion/upload-init → pre-signed PUT URL(s) + EdiConversionFile row IDs
 *   2. PUT file bytes directly to S3 (via XMLHttpRequest, with progress callback)
 *   3. POST /edi-conversion/upload-complete → backend HEAD-verifies and kicks off async parse
 *
 * The caller still polls the returned row IDs via the existing
 * getEdiConversionFileByIdQuery — uploadStatus + claimProcessingStatus surface progress
 * through the parse and post-parse stages.
 */
export function useDirectEdiUpload() {
  const [initiate] = useInitiateEdiUploadBatchMutation();
  const [confirm] = useConfirmEdiUploadBatchMutation();

  const upload = useCallback(
    async (params: IDirectUploadParams): Promise<IDirectUploadResult> => {
      const { file, file837, displayName, description, mappings, onProgress } =
        params;
      const totalBytes = file.size + (file837?.size ?? 0);

      const initResult = await initiate({
        displayName,
        description,
        originalFilename: file.name,
        fileSizeBytes: file.size,
        has837: !!file837,
        // Pass the 837's real filename + size so the backend can size-check it post-PUT and
        // keep the real extension on the S3 key (zip detection runs by magic bytes, but
        // keeping the extension makes the artifacts saner to inspect).
        ...(file837 && {
          original837Filename: file837.name,
          file837SizeBytes: file837.size,
        }),
        mappings,
      }).unwrap();

      const { uploadUrl, upload837Url, uploadBatchId, ediConversionFileIds } =
        initResult.data;

      // 835 PUT
      await putToS3WithProgress(uploadUrl, file, (loaded) => {
        onProgress?.({ loaded, total: totalBytes, currentLabel: "835" });
      });

      // 837 PUT (if present). The backend signs a separate URL per file.
      if (file837) {
        if (!upload837Url) {
          throw new Error(
            "Backend did not return upload837Url despite has837=true"
          );
        }
        await putToS3WithProgress(upload837Url, file837, (loaded) => {
          // Continue from where the 835 PUT left off so the bar keeps advancing.
          onProgress?.({
            loaded: file.size + loaded,
            total: totalBytes,
            currentLabel: "837",
          });
        });
      }

      await confirm({ uploadBatchId }).unwrap();

      return { uploadBatchId, ediConversionFileIds };
    },
    [initiate, confirm]
  );

  return { upload };
}
